/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

export const ACTIONS = {
  tap: 'tap',
  screenView: 'view',
};

export const EVENT_NAMES = {
  logout: 'aa_acc_logout',
  updatePersonalDetails: 'aa_acc_updatepersonaldetails',
  updateTravelDetails: 'aa_acc_updatetraveldetails',
  updateContactEmail: 'aa_acc_updatecontactdetailsemailaddress',
  updateContactMobile: 'aa_acc_updatecontactdetailsmobilenumber',
  changePassword: 'aa_account_changepassword',
  addPasskey: 'aa_account_addpasskey',
  deletePasskey: 'aa_account_deletepasskey',
  addNewMember: 'aa_accounts_addnewmember',
  profileFound: 'aa_accounts_profilefound',
  addMember: 'aa_accounts_addmember',
  deleteMember: 'aa_accounts_deletemember',
  deleteMemberSuccessful: 'aa_accounts_deletemembersuccessful',
  addNewCard: 'aa_accounts_addnewcard',
  saveNewCard: 'aa_accounts_savenewcard',
  removeCard: 'aa_accounts_removecard',
  ssoOtpSubmitted: 'aa_sso_otpsubmitted',
  ssoLoginSuccessful: 'aa_sso_login_successful',
  ssoSignupSuccessful: 'aa_sso_signup_successful',
  signupActivationSuccessful: 'signup_activation_successful',
  ssoPasswordResetSuccessful: 'aa_sso_pw_reset_successful',
  errors: 'errors',
};

export const CATEGORY = {
  accounts: 'accounts',
  socialProfile: 'accounts|socialProfile',
  quicklinks: 'accounts|quicklinks',
  logout: 'accounts|logout',
  menuModule: (module: string) => `accounts|${module}`,
  manageAccount: 'manageaccount',
  manageAccountModule: (module: string) => `manageaccount|${module}`,
  manageAccountUpdatePersonalDetails: 'manageaccount|updatepersonaldetails',
  manageAccountUpdateTravelDetails: 'manageaccount|updatetraveldetails',
  personalDetailsForm: 'personaldetailsform',
  travelDetailsForm: 'traveldetailsform',
  contactEmailForm: 'contactdetailsemailaddressform',
  contactEmailFormUpdate:
    'contactdetailsemailaddressformV2|updatecontactdetailsemailaddress',
  contactMobileForm: 'contactdetailsmobilenumberform',
  contactMobileFormUpdate:
    'contactdetailsmobilenumberformV2|updatecontactdetailsmobilenumber',
  supportFeedback: 'supportfeedback',
  supportFeedbackModule: (module: string) => `supportfeedback|${module}`,
  shareFeedbackForm: 'sharefeedbackform',
  aboutUs: 'aboutus',
  get aboutUsModule() {
    return (module: string) => `${this.aboutUs}|${module}`;
  },
  termsAndConditions: 'termsandconditions',
  get termsAndConditionsModule() {
    return (module: string) => `${this.termsAndConditions}|${module}`;
  },
  partners: 'partners',
  get partnersModule() {
    return (module: string) => `${this.partners}|${module}`;
  },
  settings: 'settings',
  get settingsModule() {
    return (module: string) => `${this.settings}|${module}`;
  },
  notificationPreferences: 'notificationpreferences',
  get notificationPreferencesModule() {
    return (module: string) => `${this.notificationPreferences}|${module}`;
  },
  travelNotification: 'travelnotification',
  deliveryNotification: 'deliverynotification',
  financeNotification: 'financenotification',
  subscriptionNotification: 'subscriptionnotification',
  otherNotification: 'othernotification',
  security: 'security',
  changePasswordForm: 'changepasswordform',
  changePassword: 'changepassword',
  recentActivity: 'recentactivity',
  recentActivityConfirmed: 'recentactivityconfirmed',
  twoFactorAuthentication: 'twofactorauthentication',
  passkey: 'passkey',
  passkeyDeleteConfirmationModal: 'passkeydeleteconfirmationmodal',
  manageTrip: 'managetrip',
  favourite: 'favourite',
  favourites: 'favourites',
  friendsAndFamily: 'friendsandfamily',
  addFriendsAndFamilySelection: 'addfriendsandfamilyselection',
  addNonLoyaltyProfileForm: 'addnonloyaltyprofileform',
  addLoyaltyProfile: 'addloyaltyprofile',
  addLoyaltyProfileModal: 'addloyaltyprofilemodal',
  addLoyaltyProfileForm: 'addloyaltyprofileform',
  friendAndFamilyDetails: 'friendandfamilydetails',
  updateLoyaltyProfile: 'updateloyaltyprofile',
  updateNonLoyaltyProfile: 'updatenonloyaltyprofile',
  loyaltyProfileForm: 'loyaltyprofileform',
  deleteProfileConfirmationModal: 'deleteprofilenconfirmationmodal',
  cancelDeleteProfile: 'canceldeleteprofile',
  payments: 'payments',
  savedCards: 'savedcards',
  saveCards: 'savecards',
  addCardForm: 'addcardform',
  addCardMoreInfo: 'addcardmoreinfo',
  cardDetails: 'carddetails',
  deleteCardConfirmationModal: 'deletecardconfirmationmodal',
  cancelDeleteCard: 'canceldeletecard',
  creditAccount: 'creditaccount',
  ssoLogin: 'sso_login',
  ssoSignup: 'sso_signup',
  passwordReset: 'passwordreset',
  ssoError: 'error|sso',
};

export const ANALYTICS_CONSTANTS = {
  ACCOUNTS: 'accounts',
  SOCIAL_PROFILE: 'socialProfile',
  LOGOUT: 'logout',
  MANAGE_ACCOUNT: 'manageaccount',
  UPDATE_PERSONAL_DETAILS: 'updatepersonaldetails',
  UPDATE_TRAVEL_DETAILS: 'updatetraveldetails',
  UPDATE_CONTACT_EMAIL: 'updatecontactdetailsemailaddress',
  UPDATE_CONTACT_MOBILE: 'updatecontactdetailsmobilenumber',
};

export const SCREEN_NAME = {
  accountsV2: 'accountV2',
  manageAccountV2: 'manageaccountV2',
  personalDetailsFormV2: 'personaldetailsformV2',
  travelDetailsFormV2: 'traveldetailsformV2',
  contactEmailFormV2: 'contactdetailsemailaddressformV2',
  contactMobileFormV2: 'contactdetailsmobilenumberformV2',
  updateContactEmailV2: 'updatecontactdetailsemailaddressV2',
  updateContactMobileV2: 'updatecontactdetailsmobilenumberV2',
  supportFeedbackV2: 'supportfeedbackV2',
  shareFeedbackFormV2: 'sharefeedbackformV2',
  aboutUsV2: 'aboutusV2',
  termsAndConditionsV2: 'termsandconditionsV2',
  partnersV2: 'partnersV2',
  settingsV2: 'settingsV2',
  currencyV2: 'currencyV2',
  languageV2: 'languageV2',
  notificationPreferencesV2: 'notificationpreferencesV2',
  emailPreferencesV2: 'emailpreferencesV2',
  pushPreferencesV2: 'pushpreferencesV2',
  mobileNotificationV2: 'mobilenotificationV2',
  travelNotificationV2: 'travelnotificationV2',
  deliveryNotificationV2: 'deliverynotificationV2',
  financeNotificationV2: 'financenotificationV2',
  subscriptionNotificationV2: 'subscriptionnotificationV2',
  otherNotificationV2: 'othernotificationV2',
  securityV2: 'securityV2',
  changePasswordFormV2: 'changepasswordformV2',
  changePasswordV2: 'changepasswordV2',
  recentActivityV2: 'recentactivityV2',
  recentActivityConfirmedV2: 'recentactivityconfirmedv2',
  twoFactorAuthenticationV2: 'twofactorauthenticationV2',
  passkeyV2: 'passkeyV2',
  passkeyDeleteConfirmationModalV2: 'passkeydeleteconfirmationmodalV2',
  manageTripV2: 'managetripV2',
  favouritesV2: 'favouritesV2',
  favouriteV2: 'favouriteV2',
  friendsAndFamilyV2: 'friendsandfamilyV2',
  addFriendsAndFamilySelectionV2: 'addfriendsandfamilyselectionV2',
  addNonLoyaltyProfileFormV2: 'addnonloyaltyprofileformV2',
  addLoyaltyProfileV2: 'addloyaltyprofileV2',
  addLoyaltyProfileModalV2: 'addloyaltyprofilemodalV2',
  addLoyaltyProfileFormV2: 'addloyaltyprofileformV2',
  friendAndFamilyDetailsV2: 'friendandfamilydetailsV2',
  loyaltyProfileFormV2: 'loyaltyprofileformV2',
  updateNonLoyaltyProfileV2: 'updatenonloyaltyprofileV2',
  deleteProfileConfirmationModalV2: 'deleteprofilenconfirmationmodalV2',
  cancelDeleteProfileV2: 'canceldeleteprofileV2',
  paymentsV2: 'paymentsV2',
  savedCardsV2: 'savedcardsV2',
  savedCardsAltV2: 'savedCardsV2',
  addCardFormV2: 'addcardformV2',
  cardDetailsV2: 'carddetailsV2',
  defaultCardFormV2: 'defaultcardformV2',
  deleteCardConfirmationModalV2: 'deletecardconfirmationmodalV2',
  cancelDeleteCardV2: 'canceldeletecardV2',
  creditAccountV2: 'creditaccountV2',
  loginV2: 'loginV2',
  signupV2: 'signupV2',
  emailOTPv2: 'emailOTPv2',
  mobileOTPv2: 'mobileOTPv2',
  updateMobileNumberV2: 'updateMobileNumberV2',
  userDetailsV2: 'userDetailsV2',
  homeV2: 'HomeV2',
  accountActivationEmailOTP: 'accountActivationEmailOTP',
  forgotPasswordV2: 'forgotpasswordV2',
  passwordResetV2: 'passwordresetV2',
};

export function buildLobFavouritesScreenName(lob: string): string {
  return `${lob.toLowerCase()}favouritesV2`;
}

/** Maps main-account menu `item.id` values to analytics module segment names. */
export const MENU_ID_TO_ANALYTICS_MODULE: Record<string, string> = {
  manage_account: 'manageaccount',
  payments: 'payments',
  support_feedback: 'supportfeedback',
  manage_trip: 'managetrip',
  settings: 'settings',
  security: 'security',
  about_us: 'aboutus',
  partners: 'partners',
};

export const MANAGE_ACCOUNT_TAP_MODULES = [
  'personaldetails',
  'traveldetails',
  'contactdetailsemailaddress',
  'contactdetailsmobilenumber',
] as const;

export type ManageAccountTapModule =
  (typeof MANAGE_ACCOUNT_TAP_MODULES)[number];

export function getManageAccountTapIndex(
  module: ManageAccountTapModule,
): number {
  return MANAGE_ACCOUNT_TAP_MODULES.indexOf(module) + 1;
}

/** Maps Support & Feedback menu `item.id` values to analytics module names. */
export const SUPPORT_FEEDBACK_ITEM_TO_ANALYTICS_MODULE: Record<string, string> =
  {
    askbo: 'askbo',
    my_cases: 'mycases',
    faq: 'support',
    share_feedback: 'sharefeedback',
    rate_app: 'rateourapp',
  };

export function supportFeedbackItemIdToModule(itemId: string): string | null {
  return SUPPORT_FEEDBACK_ITEM_TO_ANALYTICS_MODULE[itemId] ?? null;
}

/** Maps About us menu `item.id` values to analytics module names. */
export const ABOUT_US_ITEM_TO_ANALYTICS_MODULE: Record<string, string> = {
  tnc: 'termsandconditions',
  terms_of_use: 'termsofuse',
  privacy_statement: 'privacystatement',
  community_guidelines: 'communityguidelines',
};

export function aboutUsItemIdToModule(itemId: string): string | null {
  return ABOUT_US_ITEM_TO_ANALYTICS_MODULE[itemId] ?? null;
}

/** Maps Terms and conditions submenu `item.id` values to analytics module names. */
export const TNC_ITEM_TO_ANALYTICS_MODULE: Record<string, string> = {
  tnc_rewards: 'airasiarewards',
  tnc_gifts: 'airasiagifts',
  tnc_hotel: 'airasiahotel',
  tnc_snap: 'airasiasnap',
  tnc_ride: 'airasiaride',
  tnc_chat: 'airasiachat',
  tnc_x_travel: 'airasiaxtravel',
  tnc_promotion: 'airasiapromotion',
  tnc_duty_free: 'dutyfree',
  tnc_movetix: 'movetix',
  tnc_flights_general: 'flightsgeneral',
  tnc_airasia_berhad: 'airasiaberhadak',
  tnc_airasia_cambodia: 'airasiacambodiakt',
  tnc_thai_airasia_fd: 'thaiairasiacoltdfd',
  tnc_thai_airasia_x_xj: 'thaiairasiaxcottdxj',
  tnc_indonesia_airasia_qz: 'ptindonesiaairasiaqz',
  tnc_philippines_airasia_z2: 'philippinesairasiaincz2',
  tnc_d7_pending_review: 'D7',
};

export function tncItemIdToModule(itemId: string): string | null {
  const lower = itemId.toLowerCase();
  return (
    TNC_ITEM_TO_ANALYTICS_MODULE[itemId] ??
    TNC_ITEM_TO_ANALYTICS_MODULE[lower] ??
    null
  );
}

export function buildLocaleGeoLabel(
  screenName: string,
  locale: string,
  geo: string,
): string {
  return `${screenName}|${locale}|${geo}`;
}

export function buildModuleTapLabel(
  module: string,
  index: number,
  locale: string,
  geo: string,
): string {
  return `${module}|${index}|${locale}|${geo}`;
}

export function menuItemIdToAnalyticsModule(menuItemId: string): string | null {
  return MENU_ID_TO_ANALYTICS_MODULE[menuItemId] ?? null;
}

/**
 * Params for a main-account menu tap. Shared because two hosts render that
 * menu: MainAccountPage on the app and on mobile web, and the permanent
 * desktop-web drawer, which is chrome rather than a screen and so fires this
 * tap without the page's screen view. Returns null for an id with no module.
 */
export function buildMainAccountMenuTapParams(
  itemId: string,
  index: number,
  locale: string,
  geo: string,
) {
  const module = menuItemIdToAnalyticsModule(itemId);
  if (!module) return null;

  return buildAnalyticsParam(
    SCREEN_NAME.accountsV2,
    CATEGORY.menuModule(module),
    buildModuleTapLabel(module, index + 1, locale, geo),
  );
}

export function buildAnalyticsParam(
  screenName: string,
  category: string,
  label: string,
) {
  return { category, label, screenName, screen_name: screenName };
}

/**
 * Generic error event, shared by web and native.
 *
 * Web  (through `createWebLogger` + GTM):
 *   { event: 'trackEventNoEcommerce', eventCategory: 'error|<type>',
 *     eventAction: 'errors', eventLabel: '<code>|<description>|<message>',
 *     service, screenName, lineofbusiness }
 * App  (through the native logger):
 *   logEvent('errors', { category, label, service, screenName, lineofbusiness })
 *
 * Only the *type* segment of the category varies, so a new kind of error never
 * needs a new event or a schema change.
 */
export const ERROR_EVENT = EVENT_NAMES.errors;

export const ERROR_TYPE = {
  api: 'api',
  authentication: 'authentication',
  /** Uncaught render/runtime crash caught by the root error boundary. */
  client: 'client',
  network: 'network',
  payment: 'payment',
  validation: 'validation',
} as const;

/**
 * Known types are listed for autocomplete only — any string is accepted, so a
 * new error type needs no change here.
 */
export type ErrorType =
  | (typeof ERROR_TYPE)[keyof typeof ERROR_TYPE]
  | (string & {});

/** `action` natively, `eventAction` on web. Constant for every error. */
const ERROR_ACTION = 'errors';
const ERROR_LOB = 'account';
/** Account itself, i.e. no backend service behind the error. */
const ERROR_SERVICE = 'ACCOUNT';
/** Used when the caller cannot name the screen the error happened on. */
const ERROR_SCREEN_NAME = 'AccountV2';

/**
 * Long enough for an API message, short enough that a stack trace or an HTML
 * error page cannot blow up the data layer.
 */
const ERROR_SEGMENT_MAX_LENGTH = 120;

/** GA/Firebase drops events with longer values. */
export const ERROR_LABEL_MAX_LENGTH = 500;

/** Login / Sign Up errors. */
export const SSO_ERROR_SOURCE = {
  lob: 'SSO',
  service: 'SSO',
} as const;

export interface ErrorAnalyticsParams {
  /** Category segment, e.g. `client` → `error|client`. */
  type: ErrorType;
  /** HTTP status, API error code, error name — whatever identifies the error. */
  code?: string | number | null;
  /** Short, stable description: status text, API code, failing component. */
  description?: string | null;
  /** What the user (or the transport) was told. */
  message?: string | null;
  service?: string;
  screenName?: string;
  lob?: string;
}

/**
 * Params as they reach `logEvent`. Natively they are forwarded to Firebase as
 * they are; on web `createWebLogger` renames `action`/`category`/`label` to
 * `eventAction`/`eventCategory`/`eventLabel` and pushes them as
 * `trackEventNoEcommerce`.
 */
export interface ErrorEventParams {
  /** Only the web mapper reads this; natively it mirrors the event name. */
  action: string;
  category: string;
  label: string;
  screenName: string;
  service: string;
  lineofbusiness: string;
}

/** Label segments are pipe-separated, so a value may not carry a pipe itself. */
const normalizeErrorSegment = (
  value: string | number | null | undefined,
): string => {
  if (value === null || value === undefined) return '';

  const flattened = String(value)
    .replace(/\s+/g, ' ')
    .split('|')
    .join('/')
    .trim();

  return flattened.slice(0, ERROR_SEGMENT_MAX_LENGTH);
};

/** Params for one error event — the only place the schema is assembled. */
export function buildErrorEventParams(
  params: ErrorAnalyticsParams,
): ErrorEventParams {
  return {
    action: ERROR_ACTION,
    category: `error|${normalizeErrorSegment(params.type)}`,
    // `<code>|<description>|<message>`, empty segments kept so the three
    // positions always mean the same thing to the report.
    label: [params.code, params.description, params.message]
      .map(normalizeErrorSegment)
      .join('|')
      .slice(0, ERROR_LABEL_MAX_LENGTH),
    screenName: params.screenName || ERROR_SCREEN_NAME,
    service: params.service || ERROR_SERVICE,
    lineofbusiness: params.lob || ERROR_LOB,
  };
}

/**
 * Params for a crash caught by a root error boundary, so web and app report
 * the same payload from their respective `onError`. The description is the
 * first frame of the component stack — the component that threw.
 */
export function buildBoundaryErrorParams(
  error: Error,
  componentStack: string,
  screenName?: string,
  source?: Pick<ErrorAnalyticsParams, 'lob' | 'service'>,
): ErrorEventParams {
  return buildErrorEventParams({
    type: ERROR_TYPE.client,
    code: error.name,
    description:
      /^\s*(?:(?:at|in)\s+)?([^\s(]+)/.exec(componentStack)?.[1] ?? '',
    message: error.message,
    screenName,
    lob: source?.lob,
    service: source?.service,
  });
}

/** Settings is the consumer for currency and language screens. */
export const SETTINGS_CONSUMER_NAME = 'settings';

export const LOCALE_CURRENCY_SELECTION_LABEL = {
  currency: 'choose a currency',
  language: 'choose a language',
} as const;

/** Maps Partners submenu `item.id` values to analytics module names. */
export const PARTNERS_ITEM_TO_ANALYTICS_MODULE: Record<string, string> = {
  drone: 'drone',
  career: 'career',
  affiliate_program: 'affliateprogram',
};

export function partnersItemIdToModule(itemId: string): string | null {
  return PARTNERS_ITEM_TO_ANALYTICS_MODULE[itemId] ?? null;
}

/** Maps Settings submenu `item.id` values to analytics module names. */
export const SETTINGS_ITEM_TO_ANALYTICS_MODULE: Record<string, string> = {
  currency: 'currency',
  language: 'language',
  notification_preferences: 'notificationpreferences',
};

export function settingsItemIdToModule(itemId: string): string | null {
  return SETTINGS_ITEM_TO_ANALYTICS_MODULE[itemId] ?? null;
}

/** Maps notification channel ids to analytics module names on the main preferences screen. */
export const NOTIFICATION_CHANNEL_TO_ANALYTICS_MODULE: Record<string, string> =
  {
    email: 'email',
    push: 'pushnotification',
    sms: 'sms',
  };

export function notificationChannelIdToModule(
  channelId: string,
): string | null {
  return NOTIFICATION_CHANNEL_TO_ANALYTICS_MODULE[channelId] ?? null;
}

/** Channel detail screen analytics keyed by channel id. */
export const NOTIFICATION_CHANNEL_DETAIL_ANALYTICS: Record<
  string,
  { screenModule: string; screenName: string }
> = {
  email: {
    screenModule: 'emailnotification',
    screenName: SCREEN_NAME.emailPreferencesV2,
  },
  push: {
    screenModule: 'pushnotification',
    screenName: SCREEN_NAME.pushPreferencesV2,
  },
  sms: {
    screenModule: 'mobilenotification',
    screenName: SCREEN_NAME.mobileNotificationV2,
  },
};

export function getNotificationChannelDetailAnalytics(channelId: string) {
  return NOTIFICATION_CHANNEL_DETAIL_ANALYTICS[channelId] ?? null;
}

/** Maps main notification category short codes to analytics module names. */
export const NOTIFICATION_CATEGORY_SHORT_CODE_TO_MODULE: Record<
  string,
  string
> = {
  TRAVEL: 'travel',
  DELIVERS: 'delivery',
  FINTECH: 'finance',
  SUBSCRIPTION: 'subscription',
  LOYALTY_AND_SUBSCRIPTION: 'subscription',
  OTHER: 'others',
  OTHERS: 'others',
  PARTNERSHIP: 'others',
};

export function notificationCategoryShortCodeToModule(
  shortCode: string,
): string | null {
  const upper = shortCode.trim().toUpperCase();
  return NOTIFICATION_CATEGORY_SHORT_CODE_TO_MODULE[upper] ?? null;
}

/** Category detail screen analytics keyed by main category short code. */
const subscriptionNotificationAnalytics = {
  category: CATEGORY.subscriptionNotification,
  screenName: SCREEN_NAME.subscriptionNotificationV2,
};

const otherNotificationAnalytics = {
  category: CATEGORY.otherNotification,
  screenName: SCREEN_NAME.otherNotificationV2,
};

export const NOTIFICATION_CATEGORY_DETAIL_ANALYTICS: Record<
  string,
  { category: string; screenName: string }
> = {
  TRAVEL: {
    category: CATEGORY.travelNotification,
    screenName: SCREEN_NAME.travelNotificationV2,
  },
  DELIVERS: {
    category: CATEGORY.deliveryNotification,
    screenName: SCREEN_NAME.deliveryNotificationV2,
  },
  FINTECH: {
    category: CATEGORY.financeNotification,
    screenName: SCREEN_NAME.financeNotificationV2,
  },
  SUBSCRIPTION: subscriptionNotificationAnalytics,
  LOYALTY_AND_SUBSCRIPTION: subscriptionNotificationAnalytics,
  OTHER: otherNotificationAnalytics,
  OTHERS: otherNotificationAnalytics,
  PARTNERSHIP: otherNotificationAnalytics,
};

export function getNotificationCategoryDetailAnalytics(
  categoryShortCode: string,
) {
  const upper = categoryShortCode.trim().toUpperCase();
  return NOTIFICATION_CATEGORY_DETAIL_ANALYTICS[upper] ?? null;
}

/**
 * Maps notification sub-preference title keys (from `notificationChannelDetailPageKey`)
 * to analytics module names on category detail screens.
 */
export const NOTIFICATION_SUB_PREFERENCE_TITLE_KEY_TO_MODULE: Record<
  string,
  string
> = {
  travelMonthly: 'monthlynewsletter',
  travelWeekly: 'weeklynewsletter',
  travelUpdate: 'updatesannouncement',
  flights: 'flights',
  hotels: 'hotels',
  snap: 'snap',
  dutyfree: 'dutyfree',
  transport: 'transport',
  deliversMonthly: 'monthlynewsletter',
  deliversWeekly: 'weeklynewsletter',
  deliversUpdate: 'updatesannouncement',
  ride: 'ride',
  fintechMonthly: 'monthlynewsletter',
  fintechWeekly: 'weeklynewsletter',
  fintechUpdate: 'updatesannouncement',
  bigpay: 'bigpay',
  tuneprotect: 'tuneprotect',
  aacards: 'airasiacards',
  aamoney: 'airasiamoney',
  bigxchange: 'xchange',
  aseanpass: 'aseanpass',
  partnerOffer: 'offersfrompartners',
  b2b: 'partnershipopportunities',
  others: 'otherpromotionscontent',
  ikhlas: 'ikhlas',
  promotionAndAnnouncements: 'updatesannouncement',
};

/** Fallback map for sub-preference internal codes when title key mapping is unavailable. */
export const NOTIFICATION_SUB_PREFERENCE_INTERNAL_CODE_TO_MODULE: Record<
  string,
  string
> = {
  TRAVEL_MONTHLY: 'monthlynewsletter',
  TRAVEL_WEEKLY: 'weeklynewsletter',
  TRAVEL_UPDATE: 'updatesannouncement',
  FLIGHTS: 'flights',
  HOTELS: 'hotels',
  SNAP: 'snap',
  DUTYFREE: 'dutyfree',
  DUTY_FREE: 'dutyfree',
  TRANSPORT: 'transport',
  DELIVERS_MONTHLY: 'monthlynewsletter',
  DELIVERS_WEEKLY: 'weeklynewsletter',
  DELIVERS_UPDATE: 'updatesannouncement',
  RIDE: 'ride',
  FINTECH_MONTHLY: 'monthlynewsletter',
  FINTECH_WEEKLY: 'weeklynewsletter',
  FINTECH_UPDATE: 'updatesannouncement',
  BIGPAY: 'bigpay',
  TUNE_PROTECT: 'tuneprotect',
  TUNEPROTECT: 'tuneprotect',
  AA_CARDS: 'airasiacards',
  AACARDS: 'airasiacards',
  AA_MONEY: 'airasiamoney',
  AAMONEY: 'airasiamoney',
  BIGXCHANGE: 'xchange',
  ASEANPASS: 'aseanpass',
  PARTNER_OFFER: 'offersfrompartners',
  B2B: 'partnershipopportunities',
  OTHERS: 'otherpromotionscontent',
  IKHLAS: 'ikhlas',
};

export function notificationSubPreferenceToModule(
  internalCode: string,
  titleCode?: string,
): string | null {
  if (titleCode) {
    const parts = titleCode.split('_').filter((part) => part.length > 0);
    if (parts.length > 0) {
      const normalized = parts.map((part) => part.toLowerCase());
      const titleKey =
        normalized.length === 1
          ? normalized[0]
          : normalized[0] +
            normalized
              .slice(1)
              .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
              .join('');
      const fromTitle =
        NOTIFICATION_SUB_PREFERENCE_TITLE_KEY_TO_MODULE[titleKey];
      if (fromTitle) return fromTitle;
    }
  }

  const upper = internalCode.trim().toUpperCase();
  return NOTIFICATION_SUB_PREFERENCE_INTERNAL_CODE_TO_MODULE[upper] ?? null;
}

export function buildConsumerModuleCategory(
  consumerName: string,
  module: string,
): string {
  return `${consumerName}|${module}`;
}

export function buildScreenValueLabel(
  screenName: string,
  value: string,
): string {
  return `${screenName}|${value.toLowerCase()}`;
}

export function buildSelectionChangeLabel(
  selectionLabel: string,
  previousValue: string,
  selectedValue: string,
): string {
  return `${selectionLabel}|${previousValue.toLowerCase()}-${selectedValue.toLowerCase()}`;
}

export const SECURITY_SCREEN_VIEW_LABEL = 'security|accountsettings';

/** Maps Security menu `item.id` values to analytics module names. */
export const SECURITY_ITEM_TO_ANALYTICS_MODULE: Record<string, string> = {
  change_password: 'changepassword',
  connected_devices: 'connecteddevices',
  recent_security_activity: 'recentsecurityactivity',
  two_factor_authentication: 'twofactorauthentication',
  two_fa: 'twofactorauthentication',
  passkeys: 'passkey',
  delete_account: 'deleteaccount',
};

export function securityItemIdToModule(itemId: string): string | null {
  return SECURITY_ITEM_TO_ANALYTICS_MODULE[itemId] ?? null;
}

export function buildSecurityModuleTapLabel(
  module: string,
  index: number,
): string {
  return `${module}|${index}`;
}

export function buildRecentActivityScreenLabel(lastLoggedIn: string): string {
  return `recentactivity|${lastLoggedIn.toLowerCase()}`;
}

export function buildRecentActivityConfirmedLabel(
  answer: 'yes' | 'no' | 'changepassword',
): string {
  return `recentactivityconfirmed|${answer}`;
}

export function securityActivityTypeToModule(activityType: string): string {
  const normalized = activityType.trim().toLowerCase().replace(/\s+/g, '_');
  if (normalized === 'password_change' || normalized === 'change_password') {
    return 'changepassword';
  }
  return normalized.replace(/_/g, '');
}

export const PASSKEY_DELETE_MODAL_ACTION = {
  cancel: 'cancel',
  delete: 'delete',
} as const;

export type TwoFactorState = 'enabled' | 'disabled';

export type PasskeyStatus = 'empty' | 'registered';

export function buildTwoFactorAuthenticationLabel(
  state: TwoFactorState,
): string {
  return `twofactorauthentication|${state}`;
}

export function getPasskeyStatus(passkeyCount: number): PasskeyStatus {
  return passkeyCount > 0 ? 'registered' : 'empty';
}

export function buildPasskeyScreenLabel(status: PasskeyStatus): string {
  return `passkey|${status}`;
}

/** Spec uses `addpaskey` (not `addpasskey`) for add/delete passkey action labels. */
export function buildPasskeyActionLabel(status: PasskeyStatus): string {
  return `addpaskey|${status}`;
}

export function buildPasskeyDeleteModalLabel(index: number): string {
  return `passkeydeleteconfirmationmodal|${index}`;
}

export function buildPasskeyDeleteModalTapLabel(
  action: (typeof PASSKEY_DELETE_MODAL_ACTION)[keyof typeof PASSKEY_DELETE_MODAL_ACTION],
): string {
  return `passkeydeleteconfirmationmodal|${action}`;
}

/** Maps Manage Trip menu `item.id` values to analytics module names. */
export const MANAGE_TRIP_ITEM_TO_ANALYTICS_MODULE: Record<string, string> = {
  family_and_friends: 'familyandfriends',
  favourites: 'favorites',
};

export function manageTripItemIdToModule(itemId: string): string | null {
  return MANAGE_TRIP_ITEM_TO_ANALYTICS_MODULE[itemId] ?? null;
}

/** Maps Favourites LOB menu `item.id` values to analytics module names. */
export const FAVOURITES_ITEM_TO_ANALYTICS_MODULE: Record<string, string> = {
  hotel: 'hotel',
  gifts: 'gifts',
  flights: 'flights',
};

export function favouritesItemIdToModule(itemId: string): string | null {
  return FAVOURITES_ITEM_TO_ANALYTICS_MODULE[itemId] ?? null;
}

/** Maps Payments menu `item.id` values to analytics module names. */
export const PAYMENTS_ITEM_TO_ANALYTICS_MODULE: Record<string, string> = {
  credit_account: 'creditaccount',
  saved_cards: 'savedcards',
};

export function paymentsItemIdToModule(itemId: string): string | null {
  return PAYMENTS_ITEM_TO_ANALYTICS_MODULE[itemId] ?? null;
}

export const ADD_MEMBER_OPTION = {
  loyalty: 'addloyaltyprofile',
  nonLoyalty: 'addnonloyaltyprofile',
} as const;

export const CARD_DETAILS_CTA = {
  setAsDefaultCard: 'setasdefaultcard',
  setDefaultCard: 'setdefaultcard',
  removeCard: 'removecard',
  keepCard: 'keepcard',
  cvv: 'cvv',
} as const;

export const SAVED_CARDS_LABEL = {
  newCard: 'newcard',
  savedCards: 'savedCards',
  defaultCardType: 'card',
  newForm: 'new',
} as const;

export function buildFriendsAndFamilyProfileLabel(
  profile: string,
  index: number,
  locale: string,
  geo: string,
): string {
  return `${profile}|${index}|${locale}|${geo}`;
}

export function buildSsoLabel(prefix: string, ssoId: string | null): string {
  return `${prefix}|${ssoId ?? 'null'}`;
}

export function buildSavedCardsScreenLabel(numberOfCards: number): string {
  return `savedcards|${numberOfCards}`;
}

export function buildCreditAccountScreenLabel(creditAmount: string): string {
  return `creditaccountV2|${creditAmount}`;
}

export function resolveMemberSsoId(member: {
  loyaltyId?: string | null;
  navitaireCustomerNumber?: string | null;
  id?: string | null;
}): string | null {
  return (
    member.loyaltyId || member.navitaireCustomerNumber || member.id || null
  );
}

export function resolveMemberProfileModule(member: {
  loyaltyId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}): string {
  if (member.loyaltyId) {
    return 'loyaltyprofile';
  }
  const name = `${member.firstName ?? ''}${member.lastName ?? ''}`
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
  return name || 'nonloyaltyprofile';
}
